import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';
import './notyf-DIw0RvBj.js';
import { e as emblaNavigation } from './navigation-DGcbxkcb.js';

/**
 * @typedef {import('embla-carousel').EmblaCarouselType} EmblaCarouselApi
 */

/**
 * Synchronize a thumbnail carousel with a main carousel.
 * Also manages tabindex and keyboard navigation for role="tablist" accessibility.
 * @param {EmblaCarouselApi} emblaApiMain - The main carousel api
 * @param {EmblaCarouselApi} emblaApiThumb - The thumbnail carousel api
 */
function emblaThumbsCarousel(emblaApiMain, emblaApiThumb) {
  const thumbSlides = emblaApiThumb.slideNodes();

  thumbSlides.forEach((thumbSlide, index) => {
    thumbSlide.addEventListener('click', () => emblaApiMain.goTo(index), false);

    thumbSlide.addEventListener('keydown', (event) => {
      const total = thumbSlides.length;

      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        const next = Math.min(index + 1, total - 1);
        emblaApiMain.goTo(next);
        thumbSlides[next].focus();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        const prev = Math.max(index - 1, 0);
        emblaApiMain.goTo(prev);
        thumbSlides[prev].focus();
      }
    });
  });

  const syncThumbState = () => {
    const previous = emblaApiMain.previousSnap();
    const selected = emblaApiMain.selectedSnap();

    thumbSlides[previous]?.setAttribute('aria-selected', 'false');
    thumbSlides[previous]?.setAttribute('tabindex', '-1');
    thumbSlides[previous]?.classList.remove('embla-thumbs__slide--selected');

    thumbSlides[selected]?.setAttribute('aria-selected', 'true');
    thumbSlides[selected]?.setAttribute('tabindex', '0');
    thumbSlides[selected]?.classList.add('embla-thumbs__slide--selected');

    emblaApiThumb.goTo(selected);
  };

  syncThumbState();
  emblaApiMain.on('select', syncThumbState).on('reinit', syncThumbState);
}

class ProductMediaThumbnails extends HTMLElement {
  /** @type {import('embla-carousel').EmblaCarouselType} */ #embla;

  #viewport = this.querySelector('.embla__viewport');
  #previousButton = this.querySelector('[data-thumbnail-prev]');
  #nextButton = this.querySelector('[data-thumbnail-next]');
  #isSynced = false;

  connectedCallback() {
    if (this.#embla) return;

    this.#initCarousel();
    if (!this.#embla) return;

    Pubsub.publish('product-media-thumbnails:ready', {
      id: this.#sliderId,
      data: { thumbnail: this },
    });
  }

  get #sliderId() {
    return this.#embla?.slideNodes()[0]?.getAttribute('aria-controls');
  }

  disconnectedCallback() {
    this.#embla?.destroy();
    this.#embla = undefined;
    this.#isSynced = false;
  }

  #initCarousel() {
    if (!this.#viewport || !this.#previousButton || !this.#nextButton) return;

    this.#embla = EmblaCarousel(this.#viewport, {
      axis: 'y',
    });

    emblaNavigation(this.#embla, this.#previousButton, this.#nextButton);
  }

  connect(mainEmbla) {
    if (!mainEmbla || !this.#embla || this.#isSynced) return;

    emblaThumbsCarousel(mainEmbla, this.#embla);
    this.#isSynced = true;
  }
}

customElements.define('product-media-thumbnails', ProductMediaThumbnails);
